import styles from './QcFlagDisplay.module.css';

export interface QcFlagDisplayProps {
  flagged: boolean | null;
  notes: string | null;
  editable?: boolean;
  onChange?: (flag: boolean, notes: string) => void;
  className?: string;
  okLabel?: string;
  flaggedLabel?: string;
  variant?: 'default' | 'inline';
  hideNotes?: boolean;
}

/**
 * QcFlagDisplay - Show/edit QC flag + comment per section
 *
 * Features:
 * - Read-only mode for Report Card
 * - Editable mode for QC Workflow
 * - One flag + one comment per section (table)
 */
export function QcFlagDisplay({
  flagged,
  notes,
  editable = false,
  onChange,
  className = '',
  okLabel,
  flaggedLabel,
  variant = 'default',
  hideNotes = false
}: QcFlagDisplayProps) {
  const displayNotes = notes ?? '';
  const isFlagged = flagged ?? false;
  const resolvedOkLabel = okLabel ?? 'No additional review needed';
  const resolvedFlaggedLabel = flaggedLabel ?? 'Additional review required';

  const handleFlagChange = (newFlagged: boolean) => {
    onChange?.(newFlagged, displayNotes);
  };

  const handleNotesChange = (newNotes: string) => {
    onChange?.(isFlagged, newNotes);
  };

  if (variant === 'inline') {
    return (
      <div className={`${styles.inlineContainer} ${className}`}>
        {editable ? (
          <button
            type="button"
            className={`${styles.inlineToggle} ${isFlagged ? styles.inlineToggleActive : ''}`}
            onClick={() => handleFlagChange(!isFlagged)}
          >
            {isFlagged ? 'Flagged for review' : 'Mark review needed'}
          </button>
        ) : (
          <span className={isFlagged ? styles.inlineLabelActive : styles.inlineLabel}>{isFlagged ? resolvedFlaggedLabel : resolvedOkLabel}</span>
        )}
        {!hideNotes && editable && (
          <textarea
            className={styles.inlineNotes}
            value={displayNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Add quick context…"
            rows={2}
          />
        )}
        {!hideNotes && !editable && displayNotes && (
          <span className={styles.inlineNoteText}>{displayNotes}</span>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.qcFlagContainer} ${className}`}>
      <div className={styles.flagRow}>
        {editable ? (
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={isFlagged}
              onChange={(e) => handleFlagChange(e.target.checked)}
              className={styles.checkbox}
            />
            <span>Check the box if additional review is necessary</span>
          </label>
        ) : (
          <span className={isFlagged ? styles.flaggedText : styles.okText}>
            {isFlagged ? resolvedFlaggedLabel : resolvedOkLabel}
          </span>
        )}
      </div>

      {!hideNotes && (
        <div className={styles.commentRow}>
          <label className={styles.commentLabel}>
            Notes
          </label>
          {editable ? (
            <textarea
              className={styles.textarea}
              value={displayNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Enter QC notes or observations..."
              rows={3}
            />
          ) : (
            <div className={styles.readOnlyComment}>
              {displayNotes || <span className={styles.emptyText}>[No comments]</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default QcFlagDisplay;
